<template>
  <grid-container-fi
    class="product-benefits-fi"
    :container="false"
  >
    <template v-if="isEditMode">
      <div
        :class="{
          'product-benefits-fi__image': true,
          'product-benefits-fi__image--dark-image': fields.darkImage?.value,
        }"
      >
        <div class="fi-relative fi-w-full">
          <img
            ref="img"
            :src="fields.image?.value?.src"
            class="fi-w-full"
            @drop="onDrop"
            @dragover="allowDrop"
          />
          <benefits-nr-fi
            v-for="(box, boxId) in boxes"
            :key="boxId"
            ref="editorBoxes"
            :draggable="isEditMode"
            :placeholder-props="placeholderProps"
            :data-source-id="boxId"
            :box="box"
            @dragstart="startDrag(boxId)"
          />
        </div>
      </div>
      <div
        :class="{
          'product-benefits-fi__texts': true,
          'product-benefits-fi__texts--dark-image': fields.darkImage?.value,
        }"
      >
        <div class="fi-mb-18 fi-flex">
          <div class="fi-mr-9">edit image:</div>
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="product-benefits-fi__editable-image"
            v-html="fields?.image?.editable"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
        <loading-indicator-wrapper
          v-if="jsonStatus !== 'clean'"
          class="fi-mb-24"
          :loading="jsonStatus === 'busy'"
        >
          <button-fi
            class="button-fi__primary !fi-bg-warn"
            @click="saveJson"
          >
            save positions
            <icon-fi
              v-if="['error', 'success'].includes(jsonStatus)"
              :icon="jsonStatus === 'error' ? 'alert-triangle' : 'check'"
            />
          </button-fi>
        </loading-indicator-wrapper>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="product-benefits-fi__headline"
          v-html="fields?.headline?.editable"
        />
        <div
          class="product-benefits-fi__subheadline"
          v-html="fields?.subheadline?.editable"
        />
        <!-- eslint-enable vue/no-v-html -->
        <div class="product-benefits-fi__list">
          <placeholder-fi
            :placeholder="benefits"
            :placeholder-props="placeholderProps"
            :wrapper-component="BenefitsWrapperFi"
          />
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="product-benefits-fi__editable-button button-fi__primary fi-mr-auto fi-mt-42"
          v-html="fields?.buttonLink?.editable"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </template>
    <template v-else>
      <div
        :class="{
          'product-benefits-fi__image': true,
          'product-benefits-fi__image--dark-image': fields.darkImage?.value,
        }"
      >
        <div class="fi-relative fi-w-full">
          <ssr-cloud-image-fi
            :field="fields.image"
            :breakpoints="breakpoints"
            :unlimited="unlimited"
            class="image-fi__img"
          />
          <benefits-nr-fi
            v-for="(box, boxId) in boxes"
            :key="boxId"
            :box="box"
            :data-source-id="boxId"
            :placeholder-props="placeholderProps"
          />
        </div>
      </div>
      <div
        :class="{
          'product-benefits-fi__texts': true,
          'product-benefits-fi__texts--dark-image': fields.darkImage?.value,
        }"
      >
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-if="fields?.headline?.value"
          class="product-benefits-fi__headline"
          v-html="fields.headline.value"
        />
        <div
          v-if="fields?.subheadline?.value"
          class="product-benefits-fi__subheadline"
          v-html="fields.subheadline.value"
        />
        <!-- eslint-enable vue/no-v-html -->
        <template v-if="useBenefitsSlider">
          <carousel-fi
            ref="carousel"
            class="product-benefits-fi__carousel carousel-fi--full-height carousel-fi--pagination-start"
            :settings="{ gap: 12 }"
            no-arrows
            @after-change="afterSlideChange"
          >
            <carousel-slide-fi
              v-for="(benefitChunk, index) in benefitsChunks"
              :key="`${index}_${benefitsChunks.length}`"
              theme="benefits-slider"
            >
              <placeholder-fi
                :placeholder="benefitChunk"
                :placeholder-props="placeholderProps"
                :wrapper-component="BenefitsCarouselWrapperFi"
              />
            </carousel-slide-fi>

            <template #footer>
              <div class="fi-mt-30 fi-flex fi-items-end fi-justify-between">
                <button-fi
                  v-if="placeholderProps.buttonLink?.value?.text"
                  :class="{
                    'button-fi__primary button-fi--fi-mt-42': true,
                    'button-fi--stretched': !isSM,
                  }"
                  :href="placeholderProps.buttonLink.value.href"
                  :target="placeholderProps.buttonLink.value.target"
                >
                  {{ placeholderProps.buttonLink.value.text }}
                </button-fi>
                <carousel-slider-controls-fi
                  v-if="isSM"
                  :carousel="carousel"
                  :dark="fields.darkImage?.value"
                />
              </div>
            </template>
          </carousel-fi>
        </template>
        <div
          v-else
          class="product-benefits-fi__list"
        >
          <placeholder-fi
            :placeholder="benefits"
            :placeholder-props="placeholderProps"
            :wrapper-component="BenefitsWrapperFi"
          />
          <div>
            <button-fi
              v-if="fields.buttonLink?.value?.text"
              :class="{
                'button-fi__primary button-fi--fi-mt-42': true,
                'button-fi--stretched': !isSM,
              }"
              :href="fields.buttonLink.value.href"
              :target="fields.buttonLink.value.target"
            >
              {{ fields.buttonLink.value.text }}
            </button-fi>
          </div>
        </div>
      </div>
    </template>
  </grid-container-fi>
</template>

<script setup lang="ts">
import {
  ButtonFi,
  CarouselFi,
  CarouselSlideFi,
  CarouselSliderControlsFi,
  IconFi,
  LoadingIndicatorWrapper,
  PlaceholderFi,
  SsrCloudImageFi,
} from 'atoms';
import {
  type ComponentPublicInstance,
  computed,
  inject,
  ref,
  useTemplateRef,
  watchEffect,
} from 'vue';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import { useFischer } from '@/composables/fischerPlugin';
import type {
  CheckboxField,
  ImageField,
  LinkField,
  SingleLineTextField,
} from '@/sitecoreFieldTypes';
import type { NamedPlaceholders, Placeholder, SitecoreState } from '@/sitecoreState';
import containerPaddings from '@/tailwind/containerPaddings';
import screenWidths from '@/tailwind/screenWidths';

import BenefitsCarouselWrapperFi from './BenefitsCarouselWrapperFi.vue';
import BenefitsNrFi from './BenefitsNrFi.vue';
import BenefitsWrapperFi from './BenefitsWrapperFi.vue';

export interface BenefitsCount {
  [dataSourceId: string]: number;
}

export interface Box {
  left: number;
  top: number;
}

export interface Boxes {
  [key: string]: Box;
}

export interface PlaceholderProps {
  startDrag: (dataSourceId: string) => void;
  boxes: Boxes;
  active: string | null;
  activate: (dataSourceId: string | number | null) => void;
  benefitsCount: BenefitsCount;
  headline?: SingleLineTextField;
  subheadline?: SingleLineTextField;
  buttonLink?: LinkField;
  carousel: boolean;
}

type ScreenWidths = typeof screenWidths;

const sitecoreState = inject('sitecoreState') as SitecoreState;
const fischer = useFischer();
const { isSM, isMD } = useBreakpoints();

const props = withDefaults(
  defineProps<{
    placeholders?: NamedPlaceholders;
    fields?: {
      json?: SingleLineTextField;
      headline?: SingleLineTextField;
      subheadline?: SingleLineTextField;
      buttonLink?: LinkField;
      carousel?: CheckboxField;
      darkImage?: CheckboxField;
      image?: ImageField;
    };
  }>(),
  {
    placeholders: () => ({}),
    fields: () => ({}),
  },
);

const dataSourceId = sitecoreState?.rendering?.dataSource?.replace(/[{}]/g, '');
const unlimited = screenWidths.lg - 2 * containerPaddings.lg;

const maxBenefits = computed(() => {
  if (props.fields.carousel?.value) {
    return 1;
  }

  return isMD.value ? 8 : 3;
});
const useBenefitsSlider = computed(() => props.fields.carousel?.value || benefits.length > 10);

const benefitsChunks = computed(() => {
  const chunks = [];
  for (let i = 0; i < benefits.length; i += maxBenefits.value) {
    let chunk = [];
    for (let j = i; j < i + maxBenefits.value && j < benefits.length; j++) {
      chunk.push(benefits[j]);
    }
    chunks.push(chunk);
  }

  return chunks;
});

const createBreakpoints = () => {
  try {
    const breakpoints = Object.keys(screenWidths).map((key: string) => {
      const keyTyped = key as keyof ScreenWidths;
      return {
        mediaWidth: screenWidths[keyTyped],
        imageWidth: screenWidths[keyTyped] - 2 * containerPaddings[keyTyped],
      };
    });
    breakpoints.sort((a, b) => b.mediaWidth - a.mediaWidth);
    return breakpoints;
  } catch (error) {
    console.error('Could not create breakpoints', error);
    return [];
  }
};
const breakpoints = createBreakpoints();

const benefits: Placeholder[] = props.placeholders['product-benefits'] || [];
const benefitsCount: BenefitsCount = Object.fromEntries(
  benefits
    .filter((benefit: Placeholder) => benefit.dataSource)
    .map((benefit, idx) => [benefit.dataSource, idx + 1]),
);

const img = ref<HTMLImageElement | null>(null);
let dragging: string | null = null;
const filterBoxes: (unfilteredBoxes: Boxes) => Boxes = (unfilteredBoxes: Boxes) => {
  if (!unfilteredBoxes) {
    return {};
  }
  const filteredBoxes = Object.entries(unfilteredBoxes).filter(([itemId]) =>
    benefits.some((benefit) => benefit.dataSource === itemId),
  );
  return Object.fromEntries(filteredBoxes);
};
const boxes = ref<Boxes>(filterBoxes(JSON.parse(props.fields.json?.value || '{}')));
benefits.forEach((benefit) => {
  const boxId = benefit?.dataSource;
  if (boxId && !boxes.value[boxId]) {
    boxes.value[boxId] = { left: 0, top: 0 };
  }
});

const jsonStatus = ref<string>('clean');
const saveJson = async () => {
  if (jsonStatus.value !== 'ready' && jsonStatus.value !== 'error') {
    console.info('save json not ready');
    return;
  }
  if (!dataSourceId) {
    jsonStatus.value = 'error';
    console.error('Could not get id');
    return;
  }
  console.info('saving json...');
  jsonStatus.value = 'busy';

  try {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    const response = await fetch(
      `/sitecore/api/ssc/item/${dataSourceId}?language=${fischer.siteLanguage()}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ json: JSON.stringify(boxes.value) }),
        headers,
      },
    );
    if (response.status !== 204) {
      throw new Error(`sitecore error ${response.status} ${response}`);
    }
    jsonStatus.value = 'success';
  } catch (error) {
    console.error(error);
    jsonStatus.value = 'error';
  }
};

const editorBoxes: Array<ComponentPublicInstance<typeof BenefitsNrFi>> = [];
const createBox = (evt: DragEvent): Box | null => {
  if (!img.value) {
    console.error('no image to drop on');
    return null;
  }
  let halfNrBoxWidth = 0;
  const boxNr = dragging ? benefitsCount[dragging] - 1 : 0;
  const boxElement = editorBoxes[boxNr];
  if (boxElement) {
    halfNrBoxWidth = boxElement.$el.getBoundingClientRect().width / 2;
  } else {
    console.error('could not find box', boxNr, editorBoxes, dragging);
  }
  const clientRect = img.value.getBoundingClientRect();
  const x = Math.round(evt.pageX - clientRect.left - window.scrollX - halfNrBoxWidth);
  const y = Math.round(evt.pageY - clientRect.top - window.scrollY - halfNrBoxWidth);
  const left = Math.round(((x + halfNrBoxWidth) / clientRect.width) * 1000); // using permille for a slightly better resolution
  const top = Math.round(((y + halfNrBoxWidth) / clientRect.height) * 1000);

  jsonStatus.value = 'ready';
  return { left, top };
};

const onClickPoint = (boxId: string | number | null) => {
  placeholderProps.value.active = boxId === null ? boxId : String(boxId);
};

const startDrag = (boxId: string | number) => {
  const boxIdString = String(boxId); // v-for insists this could be a number
  dragging = boxIdString;
  placeholderProps.value.active = boxIdString;
};

const placeholderProps = ref<PlaceholderProps>({
  startDrag,
  boxes: boxes.value,
  active:
    (props.fields.carousel?.value && !fischer.app.isEditMode() && benefits[0]?.dataSource) || '',
  activate: onClickPoint,
  benefitsCount,
  headline: props.fields.headline,
  subheadline: props.fields.subheadline,
  buttonLink: props.fields.buttonLink,
  carousel: Boolean(props.fields.carousel?.value),
});

const onDrop = (evt: DragEvent) => {
  evt.preventDefault();
  if (dragging !== null) {
    const point = createBox(evt);
    if (point) {
      boxes.value[dragging] = point;
    }
  }
  dragging = null;
};

const allowDrop = (evt: DragEvent) => {
  evt.preventDefault();
};

const getCurrentSlideIndex = (activeItem: string) => {
  return Math.ceil(benefitsCount[activeItem] / maxBenefits.value) - 1;
};

const carousel = useTemplateRef('carousel');
const afterSlideChange = ({ newSlideIndex }: { newSlideIndex: number }) => {
  if (!props.fields.carousel?.value) {
    return;
  }

  if (placeholderProps.value.active) {
    const currentSlideIndex = getCurrentSlideIndex(placeholderProps.value.active);
    if (newSlideIndex !== currentSlideIndex) {
      placeholderProps.value.active = benefitsChunks.value[newSlideIndex][0].dataSource || null;
    }
  } else {
    placeholderProps.value.active = benefitsChunks.value[newSlideIndex][0].dataSource || null;
  }
};
watchEffect(() => {
  if (IS_SSR || !carousel.value || !placeholderProps.value.active) {
    return;
  }

  const carouselIndex = getCurrentSlideIndex(placeholderProps.value.active);
  const index: number | undefined = carousel.value.getIndex();
  if (index === carouselIndex || index === undefined) {
    return;
  }
  carousel.value.go(carouselIndex);
});
</script>

<style scoped src="./product-benefits-fi" />
